import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { adminAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Database, Loader2, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, ExternalLink, Lock, History } from 'lucide-react';
import { toast } from 'sonner';
import { BackupRestoreDialog } from '../components/BackupRestoreDialog';

const STATUS_BADGES = {
  success: { label: 'Success', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  failed: { label: 'Failed', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertTriangle },
  running: { label: 'Running', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: Loader2 },
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const AdminBackups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [restoreFor, setRestoreFor] = useState(null);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listBackups();
      setBackups(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBackups(); }, []);

  const runNow = async () => {
    if (!window.confirm('Run a one-shot encrypted backup to SharePoint now? This may take 30–90 seconds.')) return;
    setRunning(true);
    try {
      const res = await adminAPI.runBackupNow();
      if (res.data?.status === 'success') {
        toast.success(`Backup uploaded: ${res.data.filename} (${fmtBytes(res.data.size_bytes)})`);
      } else {
        toast.error(`Backup failed: ${res.data?.error || 'unknown error'}`);
      }
      fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup trigger failed');
    } finally {
      setRunning(false);
    }
  };

  const lastSuccess = backups.find(b => b.status === 'success');
  const lastFailure = backups.find(b => b.status === 'failed');

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-backups-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-3">
              <Database className="w-7 h-7 text-blue-600" />
              CRM Backups
            </h1>
            <p className="text-slate-500 mt-1">
              Nightly encrypted backups of the full CRM database, uploaded to your SharePoint{' '}
              <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Helios CRM Backups</code> folder. 90-day retention.
            </p>
          </div>
          <Button
            onClick={runNow}
            disabled={running}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="run-backup-now-btn"
          >
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run Backup Now
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200" data-testid="backup-stat-last-success">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Last Successful</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {lastSuccess ? formatDate(lastSuccess.created_at) : 'Never'}
                  </p>
                  {lastSuccess && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {fmtBytes(lastSuccess.size_bytes)} · {lastSuccess.document_count || 0} docs
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="backup-stat-encryption">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Encryption</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">Fernet (AES-128)</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Key in <code className="font-mono text-[10px]">BACKUP_ENCRYPTION_KEY</code> env var
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="backup-stat-retention">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Retention</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">90 days</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Older backups auto-deleted from SharePoint
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {lastFailure && (!lastSuccess || lastFailure.created_at > lastSuccess.created_at) && (
          <Card className="border-rose-200 bg-rose-50" data-testid="backup-warning-failed">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-rose-900">Most recent backup attempt failed</p>
                <p className="text-rose-700 mt-0.5">{lastFailure.error || 'Unknown error'}</p>
                <p className="text-rose-500 text-xs mt-1">{formatDate(lastFailure.created_at)}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center justify-between">
              <span>Backup History</span>
              <Button variant="ghost" size="sm" onClick={fetchBackups} data-testid="refresh-backups-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : backups.length === 0 ? (
              <p className="text-center text-slate-400 py-12 text-sm">
                No backups yet. Click <strong>Run Backup Now</strong> to create the first one or wait for the nightly scheduler (02:00 UTC).
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>Triggered By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((b) => {
                    const meta = STATUS_BADGES[b.status] || STATUS_BADGES.failed;
                    const Icon = meta.icon;
                    return (
                      <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                        <TableCell className="text-sm">{formatDate(b.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={`${meta.cls} border text-[10px] flex items-center gap-1 w-fit`}>
                            <Icon className={`w-3 h-3 ${b.status === 'running' ? 'animate-spin' : ''}`} />
                            {meta.label}
                          </Badge>
                          {b.status === 'failed' && b.error && (
                            <p className="text-[10px] text-rose-600 mt-0.5 max-w-xs truncate" title={b.error}>{b.error}</p>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{b.filename || '—'}</TableCell>
                        <TableCell className="text-xs">{fmtBytes(b.size_bytes)}</TableCell>
                        <TableCell className="text-xs">{b.document_count?.toLocaleString() || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{b.triggered_by || 'scheduler'}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1.5 justify-end">
                            {b.status === 'success' && b.sharepoint_item_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                onClick={() => setRestoreFor(b)}
                                data-testid={`backup-restore-${b.id}`}
                              >
                                <History className="w-3 h-3 mr-1" /> Restore
                              </Button>
                            )}
                            {b.sharepoint_web_url && (
                              <a
                                href={b.sharepoint_web_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 inline-flex items-center text-xs"
                                data-testid={`backup-open-${b.id}`}
                              >
                                Open <ExternalLink className="w-3 h-3 ml-1" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <BackupRestoreDialog
          open={!!restoreFor}
          onOpenChange={(o) => !o && setRestoreFor(null)}
          backup={restoreFor}
          onRestored={() => fetchBackups()}
        />
      </div>
    </Layout>
  );
};

export default AdminBackups;
